<template>
  <div class="fm-app">
    <!-- ── Slim accent bar ── -->
    <div class="fm-app__topbar"/>

    <!-- ── Debt helpline warning (required disclosure) — below the site nav, above the page title ── -->
    <DebtHelplineBanner/>

    <!-- ── Page header ── -->
    <header class="fm-app__header">
      <a v-if="isAuthenticated" href="/system/dashboard" class="btn-fm btn-fm-back">
        <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z" fill-rule="evenodd"/>
        </svg>
        <span>Back to Flashboard</span>
      </a>

      <div class="fm-app__header-text">
        <h1 class="fm-app__title">{{ pageTitle }}</h1>
        <p class="fm-app__subtitle">{{ pageSubtitle }}</p>
      </div>
    </header>

    <!-- ── Unique link notice ── -->
    <div class="fm-app__container">
      <Callout :visible="uniqueReminderState" @close="uniqueReminderClosed" dismissible kind="primary" class="callout-unique-info">
        This link is unique to your application. Bookmark this page to continue later.
      </Callout>

      <!-- ── Main card ── -->
      <div class="fm-card">

        <!-- Step progress bar -->
        <div class="fm-card__progress">
          <StepProgress :current-step="visibleCurrentIndex" :steps="visibleStepNames"/>
        </div>

        <!-- Step title -->
        <div ref="stepHeader" class="fm-card__step-title">
          <span class="fm-card__step-number">{{ visibleCurrentIndex + 1 }}</span>
          <h2 class="fm-card__step-name">{{ steps.list[steps.current] }}</h2>
          <span class="fm-card__step-badge">{{ visibleCurrentIndex + 1 }} / {{ visibleStepNames.length }}</span>
        </div>

        <!-- Step body -->
        <div ref="applicationSteps" class="fm-card__body">
          <TransitionGroup class="d-block" mode="out-in" name="fm-fade" tag="div">
            <Component
              :is="name"
              v-for="(name, idx) in stepViews"
              v-if="!skippedStepIndices.has(idx)"
              v-show="idx === steps.current"
              :key="name"
              ref="steps"
              :loan-number="loanNumber"
            >
              <template v-slot:controls="{ next, prev }">
                <Portal v-if="idx === steps.current" to="new-loan-application-controls">
                  <button
                    v-if="prev && previousButtonText && !isLocked"
                    :disabled="disableActionButtons"
                    class="btn-fm btn-fm-outline"
                    @click.stop.prevent="prev"
                  >
                    <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z" fill-rule="evenodd"/>
                    </svg>
                    <span v-text="previousButtonText"/>
                  </button>

                  <button
                    v-if="next && !isLastStep || !isLocked"
                    :disabled="disableActionButtons"
                    class="btn-fm btn-fm-primary"
                    @click.stop.prevent="next"
                  >
                    <span>{{ nextButtonText }}</span>
                    <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z" fill-rule="evenodd"/>
                    </svg>
                  </button>

                  <a v-if="isLastStep" class="btn-fm btn-fm-primary" :href="finalCtaHref">
                    <span>{{ finalCtaLabel }}</span>
                    <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z" fill-rule="evenodd"/>
                    </svg>
                  </a>
                </Portal>
              </template>
            </Component>
          </TransitionGroup>
        </div>
      </div>

      <!-- ── Sticky bottom nav ── -->
      <div class="fm-bottom-nav" :class="{ 'fm-bottom-nav--stuck': navIsStuck }">
        <PortalTarget :multiple="true" class="fm-bottom-nav__inner" name="new-loan-application-controls"/>
      </div>
      <!-- Sentinel: when this is off-screen the nav is stuck over the card -->
      <div ref="navSentinel" class="fm-nav-sentinel"/>

      <LoadingPlaceholder :loading="steps.meta.loading" :message="stepLoadingMessage" dim/>
    </div>
  </div>
</template>

<script>
import LoanDetails from './steps/LoanDetails/LoanDetails';
import ContactVerification from './steps/ContactVerification/ContactVerification';
import MoreAboutYourself from './steps/MoreAboutYourself/MoreAboutYourself';
import EmploymentDetails from './steps/EmploymentDetails/EmploymentDetails';
import BankingInformation from './steps/BankingInformation/BankingInformation';
import BankVerification from './steps/BankVerification/BankVerification';
import Review from './steps/Review/Review';
import ThankYou from './steps/ThankYou';
import {NEW_APPLICATION} from '@store/constants';
import LoadingPlaceholder from '@modules/shared/components/LoadingPlaceholder.vue';
import {scrollToElement} from '@modules/shared/helpers/form';
import StepProgress from '@modules/shared/components/StepProgress';
import store from '@modules/guest/new-applicant/store/store';
import Password from './steps/Password/Password';
import Callout from '@modules/shared/components/display/Callout';
import DebtHelplineBanner from '@modules/shared/components/display/DebtHelplineBanner.vue';
import {ApplicationChannel} from '../constants';

const StepComponentMap = {
  LoanDetails,
  ContactVerification,
  MoreAboutYourself,
  EmploymentDetails,
  BankingInformation,
  Review,
  Password,
  BankVerification,
  ThankYou,
};

const buildStepComponentMap = ({skipPassword, skipBankVerification, skipContactVerification}) => {
  const map = { LoanDetails };
  if (!skipContactVerification) map.ContactVerification = ContactVerification;
  map.MoreAboutYourself = MoreAboutYourself;
  map.EmploymentDetails = EmploymentDetails;
  map.BankingInformation = BankingInformation;
  map.Review = Review;
  if (!skipPassword) map.Password = Password;
  if (!skipBankVerification) map.BankVerification = BankVerification;
  map.ThankYou = ThankYou;
  return map;
};

const buildStepNames = ({skipPassword, skipBankVerification, skipContactVerification}) => {
  const names = ['Loan Details'];
  if (!skipContactVerification) names.push('Contact Verification');
  names.push('More About Yourself', 'Employment Details', 'Banking Information', 'Review');
  if (!skipPassword) names.push('Password');
  if (!skipBankVerification) names.push('Bank Verification');
  names.push('Done');
  return names;
};

const gtagPayload = (step, stepNames) => {
  const stepName = stepNames[step];
  if (!stepName) return [null, null];
  const action = (step + 1) + '_' + stepName.toLowerCase().replaceAll(' ', '_');
  return [action, { 'event_category': 'draft_loan_application', 'event_label': 'Draft Loan Application' }];
};

const SHOW_UNIQUE_REMINDER = (() => {
  const reminder = window.localStorage.getItem('uniqueReminderState');
  if (reminder === null) return true;
  return Boolean(Number(reminder));
})();

export default {
  name: 'LoanApplication',
  components: {Callout, DebtHelplineBanner, StepProgress, LoadingPlaceholder, ...StepComponentMap},
  props: {
    isAuthenticated: { type: Boolean, default: false },
    hasRecentCreditSenseReport: { type: Boolean, default: false },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    hasRecentCreditSenseReport: {
      type: Boolean,
      default: false,
    },
    hasMobileNumber: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loanNumber: null,
      firstLoad: true,
      gclid: null,
      uniqueReminderState: SHOW_UNIQUE_REMINDER,
      navIsStuck: false,
    };
  },
  computed: {
    isLocked() {
      return store.getters[NEW_APPLICATION.GETTERS.IS_LOCKED];
    },
    isTopUp() {
      const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
      return cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_TOP_UP;
    },
    isReApply() {
      const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
      return cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_RE_APPLY;
    },
    requiresBankVerification() {
      if (!this.isTopUp) return true;
      const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
      return cache?.is_creditsense_required !== false;
    },
    skippedStepIndices() {
      if (!this.isTopUp) return new Set();
      const skipped = [];
      if (!this.requiresBankVerification) {
        const bvIndex = this.activeStepNames.indexOf('Bank Verification');
        if (bvIndex !== -1) skipped.push(bvIndex);
      }
      return new Set(skipped);
    },
    visibleStepNames() {
      return this.steps.list.filter((_, idx) => !this.skippedStepIndices.has(idx));
    },
    visibleCurrentIndex() {
      let visible = 0;
      for (let i = 0; i < this.steps.current; i++) {
        if (!this.skippedStepIndices.has(i)) visible++;
      }
      return visible;
    },
    canPerformCS() {
      return store.state[NEW_APPLICATION.MODULE_NAME].meta.canPerformCS;
    },
    steps() {
      return store.state[NEW_APPLICATION.MODULE_NAME].steps;
    },
    skipPassword() { return this.isAuthenticated; },
    skipBankVerification() { return this.isAuthenticated && this.hasRecentCreditSenseReport; },
    skipContactVerification() { return this.isAuthenticated }, //&& this.hasMobileNumber; 
    stepFlags() {
      return {
        skipPassword: this.skipPassword,
        skipBankVerification: this.skipBankVerification,
        skipContactVerification: this.skipContactVerification,
      };
    },
    activeStepComponentMap() { return buildStepComponentMap(this.stepFlags); },
    activeStepNames() { return buildStepNames(this.stepFlags); },
    bankVerificationIndex() { return this.activeStepNames.indexOf('Bank Verification'); },
    thankYouIndex() { return this.activeStepNames.indexOf('Done'); },
    step() {
      if (this.isLocked && this.canPerformCS) {
        return this.bankVerificationIndex !== -1 ? this.bankVerificationIndex : this.thankYouIndex;
      } else if (this.isLocked) {
        return this.thankYouIndex;
      }
      return this.steps.current;
    },
    meta() { return store.state[NEW_APPLICATION.MODULE_NAME].meta; },
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    stepViews() { return Object.values(this.activeStepComponentMap).map(c => c.name); },
    nextButtonText() { return this.activeStepNames[this.steps.current + 1] || this.finalCtaLabel; },
    previousButtonText() { return this.activeStepNames[this.steps.current - 1] || null; },
    isLastStep() { return this.steps.current === this.activeStepNames.length - 1; },
    disableActionButtons() { return this.steps.meta.loading; },
    pageTitle() {
      if (this.isTopUp) return 'Top-Up Application';
      if (this.isReApply || this.isAuthenticated) return 'Re-apply for a Loan';
      return 'Loan Application';
    },
    pageSubtitle() {
      if (this.isTopUp) return "Welcome back! Most fields are already filled in for you.";
      if (this.isReApply || this.isAuthenticated) return 'Welcome back. Confirm your details to submit a new application.';
      return "Quick, easy, and transparent — let's get you sorted.";
    },
    finalCtaLabel() { return this.isAuthenticated ? 'Home' : 'Login'; },
    finalCtaHref() { return this.isAuthenticated ? '/system/dashboard' : '/login'; },
    stepLoadingMessage() {
      const stepName = this.activeStepNames[this.steps.current];
      if (stepName === 'Review') return 'Submitting your application…';
      return 'Saving your details…';
    },
  },
  watch: {
    ['steps.current']: {
      immediate: true,
      handler(current, previous) {
        if (this.skippedStepIndices.has(current)) {
          const direction = (previous === undefined || current > previous) ? +1 : -1;
          let target = current + direction;
          while (target >= 0 && target < this.steps.list.length && this.skippedStepIndices.has(target)) {
            target += direction;
          }
          target = Math.max(0, Math.min(target, this.steps.list.length - 1));
          if (target !== current) {
            store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_CURRENT_STEP, payload: target });
            return;
          }
        }

        if (current < this.step) {
          store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_CURRENT_STEP, payload: this.step });
          return;
        }

        if (!previous || current > previous) {
          const [action, payload] = gtagPayload(current, this.activeStepNames);
          if (payload && action) this.$gtag(action, payload);
        }

        this.$nextTick(() => {
          setTimeout(() => {
            if (this.firstLoad || current === this.steps.list.length - 1) {
              window.scroll({ top: 0 });
              this.firstLoad = false;
            } else {
              scrollToElement(this.$refs.stepHeader);
            }
          }, 100);
        });
      },
    },
  },
  mounted() {
    this._setupNavStickObserver();
  },

  beforeDestroy() {
    if (this._navObserver) this._navObserver.disconnect();
  },

  methods: {
    uniqueReminderClosed() {
      window.localStorage.setItem('uniqueReminderState', '0');
    },

    _setupNavStickObserver() {
      const sentinel = this.$refs.navSentinel;
      if (!sentinel || typeof IntersectionObserver === 'undefined') return;

      // When the sentinel element (just below the nav's natural position) exits
      // the viewport below, the nav has lifted off and is now stuck over the card.
      this._navObserver = new IntersectionObserver(
        ([entry]) => { this.navIsStuck = !entry.isIntersecting; },
        { threshold: 0 },
      );
      this._navObserver.observe(sentinel);
    },
  },
  created() {
    if (typeof window.gclid !== 'function') return;
    gclid().then((client_id) => { this.gclid = client_id; });
  },
};
</script>

<style lang="scss">
// All shell-level CSS (design tokens, buttons, app/card frame, sticky nav,
// fade transitions, RadioButtonGroup override, ThankYou timeline) now lives
// in the shared partial below — mirrored 1:1 into bought-lead's
// LeadLoanApplication.vue via the same @import, instead of two hand-kept
// copies drifting apart. See that file for why.
@import '../../../shared/styles/fm-design-system';
</style>
